import { ComponentType, lazy } from "react";

/**
 * Enhanced lazy loading with automatic retry logic for chunk load errors
 *
 * @param importFunc - Dynamic import function
 * @param retries - Number of retry attempts (default: 3)
 * @param delay - Delay between retries in ms (default: 1000)
 * @returns Lazy loaded component with retry logic
 *
 * @example
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): React.LazyExoticComponent<T> {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attemptImport = (retriesLeft: number) => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            // Check if it's a chunk load error
            const isChunkError =
              error.name === "ChunkLoadError" ||
              error.message?.includes("Failed to fetch dynamically imported module") ||
              error.message?.includes("Importing a module script failed") ||
              error.message?.toLowerCase().includes("failed to fetch");

            if (isChunkError && retriesLeft > 0) {
              console.warn(
                `Chunk load failed. Retrying... (${retries - retriesLeft + 1}/${retries})`
              );

              setTimeout(() => {
                attemptImport(retriesLeft - 1);
              }, delay);
            } else if (isChunkError && retriesLeft === 0) {
              // All retries exhausted, suggest reload
              console.error(
                "Failed to load module after multiple retries. This may be due to a deployment update. Reloading the page may help."
              );
              reject(
                new Error(
                  "Failed to load module. The application may have been updated. Please reload the page."
                )
              );
            } else {
              // Not a chunk error, reject immediately
              reject(error);
            }
          });
      };

      attemptImport(retries);
    });
  });
}

/**
 * Preload a lazy component to improve perceived performance
 *
 * @param lazyComponent - Lazy component to preload
 *
 * @example
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 * preloadComponent(MyComponent); // Preload on hover or other trigger
 */
export function preloadComponent(lazyComponent: React.LazyExoticComponent<any>) {
  // @ts-expect-error - _payload is internal React property
  const component = lazyComponent._payload;
  if (component && component._status === -1) {
    component._init(component._payload);
  }
}
